import { useEffect, useRef } from 'react';

const SECRET = 'EDC';
const WINDOW_MS = 3000;

// Listens for the user to type "EDC" within WINDOW_MS at any point. Fires
// `onTrigger` regardless of current EDC mode — the caller is expected to be a
// toggle that handles both "melt in" and "drip out" transitions.
// Ignores typing inside text inputs / textareas / contentEditable so it
// doesn't fire while someone is legitimately typing in a form.
// The `enabled` flag is kept as a test affordance; production callers should
// leave it `true`.
export default function useEdcSecret(onTrigger, { enabled = true } = {}) {
  const bufferRef = useRef([]);

  useEffect(() => {
    if (!enabled) return undefined;

    const isTypingTarget = (target) => {
      if (!target) return false;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (target.isContentEditable) return true;
      return false;
    };

    const handleKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const key = (e.key || '').toUpperCase();
      if (key.length !== 1) return;

      const now = Date.now();
      const buf = bufferRef.current.filter((k) => now - k.t <= WINDOW_MS);
      buf.push({ k: key, t: now });

      // Keep buffer trimmed to last N chars equal to secret length so we
      // match the trailing sequence and tolerate noise before it.
      while (buf.length > SECRET.length) buf.shift();

      bufferRef.current = buf;

      const typed = buf.map((b) => b.k).join('');
      if (typed === SECRET) {
        const span = buf[buf.length - 1].t - buf[0].t;
        if (span <= WINDOW_MS) {
          bufferRef.current = [];
          onTrigger();
        }
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onTrigger, enabled]);
}
